export type AccountPlan = 'free' | 'starter' | 'creator' | 'studio';
export type PaidPlan = Exclude<AccountPlan, 'free'>;
export type BillingInterval = 'monthly' | 'yearly';

export const FREE_ACCOUNT_CREDITS = 15;

export const PLAN_MONTHLY_CREDITS: Record<PaidPlan, number> = {
  starter: 250,
  creator: 750,
  studio: 1800
};

export const PLAN_ACCESS: Record<AccountPlan, { maxScale: 4 | 8; maximumDetail: boolean }> = {
  free: { maxScale: 4, maximumDetail: true },
  starter: { maxScale: 4, maximumDetail: true },
  creator: { maxScale: 8, maximumDetail: true },
  studio: { maxScale: 8, maximumDetail: true }
};

export function normalizePlan(value: unknown): AccountPlan {
  return value === 'starter' || value === 'creator' || value === 'studio' ? value : 'free';
}

export function effectivePlan(plan: unknown, subscriptionStatus?: unknown): AccountPlan {
  const normalized = normalizePlan(plan);
  if (normalized === 'free') return 'free';
  const status = typeof subscriptionStatus === 'string' ? subscriptionStatus.trim().toLowerCase() : '';
  // Paid feature access is granted only after a verified active subscription
  // webhook. Pending, on-hold, failed, cancelled and expired states fall back
  // to Free access even when a paid plan name remains on the billing profile.
  return status === 'active' ? normalized : 'free';
}

export function planAllows8x(plan: unknown, subscriptionStatus?: unknown) {
  return PLAN_ACCESS[effectivePlan(plan, subscriptionStatus)].maxScale >= 8;
}

export function planAllowsMaximumDetail(plan: unknown, subscriptionStatus?: unknown) {
  return PLAN_ACCESS[effectivePlan(plan, subscriptionStatus)].maximumDetail;
}

export function subscriptionCreditGrant(plan: PaidPlan, billing: BillingInterval) {
  const monthly = PLAN_MONTHLY_CREDITS[plan];
  return billing === 'yearly' ? monthly * 12 : monthly;
}

function normalizeScale(value: unknown, fallback: 1 | 2 = 2): 1 | 2 | 4 | 8 {
  const scale = Number(value ?? fallback);
  if (scale >= 8) return 8;
  if (scale >= 4) return 4;
  if (scale >= 2) return 2;
  return 1;
}

function safeMegapixels(value: unknown) {
  const megapixels = Number(value);
  if (!Number.isFinite(megapixels)) return 1;
  return Math.min(100, Math.max(0.25, megapixels));
}

export function estimateJobCredits(
  tool: string,
  options: Record<string, string | number | boolean> = {},
  megapixels = 1
) {
  const mp = safeMegapixels(megapixels);
  const scale = normalizeScale(options.scale, tool === 'enhance' ? 1 : 2);

  switch (tool) {
    case 'enhance': {
      const strength = Math.min(100, Math.max(25, Number(options.detailStrength ?? 70)));
      const strengthExtra = strength >= 90 ? 2 : strength >= 65 ? 1 : 0;
      const scaleExtra = scale === 8 ? 5 : scale === 4 ? 2 : scale === 2 ? 1 : 0;
      return 3 + strengthExtra + scaleExtra + Math.ceil(mp * 0.9);
    }
    case 'upscale': {
      const base = scale === 8 ? 7 : scale === 4 ? 4 : 2;
      const rate = scale === 8 ? 0.9 : scale === 4 ? 0.5 : 0.25;
      return base + Math.ceil(mp * rate);
    }
    case 'face-restore':
      return Math.max(3, Math.ceil(mp));
    case 'deblur':
    case 'denoise':
      return Math.max(5, Math.ceil(mp * 2.5));
    case 'restore':
    case 'relight':
      return 6;
    case 'product-photo':
      return Math.max(6, Math.ceil(mp * 1.25) + 2);
    case 'remove-background':
      return 3;
    case 'vectorize':
      return 2;
    case 'object-remover':
      return 5;
    case 'text-remover':
    case 'background-changer':
    case 'photo-colorizer':
      return 6;
    case 'image-editor':
      return 5;
    case 'image-extender': {
      const amount = Math.max(160, Math.min(560, Number(options.amount ?? 320)));
      const sides = String(options.direction ?? 'right') === 'all' ? 4 : 1;
      const expansionFactor = amount >= 560 ? 3 : amount >= 320 ? 2 : 1;
      return 5 + expansionFactor + (sides === 4 ? 2 : 0) + Math.max(0, Math.ceil(mp / 12));
    }
    case 'compress':
    case 'resize':
    case 'crop':
    case 'convert':
    case 'social-resize':
    case 'color-picker':
    case 'palette-generator':
    case 'metadata-remover':
      return 0;
    case 'sharpen':
      return mp > 20 ? 2 : 1;
    default:
      return 3;
  }
}
